import React from 'react';
import PropTypes from 'prop-types';
import BlueBookButton from 'chameleon/packages/blue-book-player/library/bluebook-button/BlueBookButton';
import BaseModal from '../BaseModal';

const ConfirmDialog = (props) => {
  const {
    children,
    title,
    subTitle,
    topBar,
    dialogClassName,
    wrapperClassName,
    contentClassName,
    headingClassName,
    topBarClassName,
    closeButtonClassName,
    showCloseButton,
    content,
    onSuccess,
    onClose,
    onSecondaryBtnClick,
    isPrimaryBtnDisabled,
    contentLabel,
    aria,
    size,
    isContainingBlock,
    isScrollableContent,
    hasTitleWithTooltip,
  } = props;
  const {
    btnPrimary,
    btnPrimaryType = 'warning',
    btnSecondaryType = 'naked',
    btnSecondary,
    bottomLeftContent,
  } = content || {};

  const _onClose = (event) => {
    onClose(event);
  };

  const isPrimaryBtn = btnPrimaryType === 'primary';
  const isPrimaryWarningBtn = btnPrimaryType === 'warning';
  const isPrimarySuccessBtn = btnPrimaryType === 'success';
  const isSecondaryBtn = btnSecondaryType === 'secondary';
  const isSecondaryWarningBtn = btnSecondaryType === 'warning';
  const isSecondaryNakedBtn = btnSecondaryType === 'naked';

  return (
    <BaseModal
      title={title}
      subTitle={subTitle}
      topBar={topBar}
      size={size}
      onClose={_onClose}
      dialogClassName={dialogClassName}
      wrapperClassName={wrapperClassName}
      contentClassName={contentClassName}
      headingClassName={headingClassName}
      topBarClassName={topBarClassName}
      closeButtonClassName={closeButtonClassName}
      showCloseButton={false}
      showDialogCloseButton={showCloseButton}
      contentLabel={contentLabel}
      aria={aria}
      contentWithSpacing={false}
      isContainingBlock={isContainingBlock}
      isScrollableContent={isScrollableContent}
      hasTitleWithTooltip={hasTitleWithTooltip}
      closeOnBackgroundClick
      returnFocus
      bottomContent={
        (btnSecondary || btnPrimary || bottomLeftContent) && (
          <div className="footer flex gap-x-[14px] justify-end">
            {bottomLeftContent}
            {btnSecondary && (
              <BlueBookButton
                isSecondary={isSecondaryBtn}
                isWarning={isSecondaryWarningBtn}
                isNaked={isSecondaryNakedBtn}
                accessibility={{
                  'data-test-id': 'button-secondary',
                  'aria-label': btnSecondary,
                }}
                onClick={onSecondaryBtnClick || onClose}
                className="!font-bold !py-[7px] !px-[23px] !h-auto !leading-[1.71em]"
              >
                {btnSecondary}
              </BlueBookButton>
            )}

            {btnPrimary && (
              <BlueBookButton
                isPrimary={isPrimaryBtn}
                isWarning={isPrimaryWarningBtn}
                isSuccess={isPrimarySuccessBtn}
                disabled={isPrimaryBtnDisabled}
                accessibility={{
                  'data-test-id': 'button-primary',
                  'aria-label': btnPrimary,
                }}
                className=" !font-bold !py-[7px] !px-[23px] !h-auto !leading-[1.71em]"
                onClick={onSuccess}
              >
                {btnPrimary}
              </BlueBookButton>
            )}
          </div>
        )
      }
    >
      {children}
    </BaseModal>
  );
};

ConfirmDialog.propTypes = {
  // The title of the modal
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // The title of the modal
  subTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // Places content in the top bar between title and X
  topBar: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // The class to style the dialog window.
  dialogClassName: PropTypes.string,
  // The class to style the modal content.
  contentClassName: PropTypes.string,
  // The class to style the modal wrapper.
  wrapperClassName: PropTypes.string,
  // The class to style the modal heading title.
  headingClassName: PropTypes.string,
  // The class to style the modal top bar.
  topBarClassName: PropTypes.string,
  // Handler for displaying X button.
  showCloseButton: PropTypes.bool,
  // Callback for submitting when confirmation buttons are available.
  onSuccess: PropTypes.func,
  // Callback for closing the modal and X button
  onClose: PropTypes.func,
  // Callback for clicking the secondary button
  onSecondaryBtnClick: PropTypes.func,
  // The accessibility content label
  contentLabel: PropTypes.string,
  // Object for setting accessibility aria options
  aria: PropTypes.object,
  // If true the content receives spacing tailwind classes (margins, paddings)
  contentWithSpacing: PropTypes.bool,
  // Sets the size of the modal
  size: PropTypes.string,
  // Sets the modal buttons options
  content: PropTypes.object,
  // Disables the primary button
  isPrimaryBtnDisabled: PropTypes.bool,
  // Transforms the wrapper container into a Containing Block
  isContainingBlock: PropTypes.bool,
  // If true it removes the scrolls from modal body
  isScrollableContent: PropTypes.bool,
  // If true the title tooltip algorithm is used to determine
  // if title needs tooltip
  hasTitleWithTooltip: PropTypes.bool,
};

ConfirmDialog.defaultProps = {
  title: <div />,
  topBar: null,
  dialogClassName: null,
  contentClassName: null,
  wrapperClassName: null,
  headingClassName: null,
  topBarClassName: null,
  showCloseButton: true,
  onSuccess: () => {},
  onClose: () => {},
  onSecondaryBtnClick: null,
  contentLabel: '',
  aria: {},
  contentWithSpacing: true,
  size: 'small',
  isPrimaryBtnDisabled: false,
  isScrollableContent: true,
  hasTitleWithTooltip: true,
};

export default ConfirmDialog;
